import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ClipboardCheck, Clock, Loader2, Package, Pencil, User, Workflow } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api, getApiErrorMessage } from "../../api/client";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { useAuth } from "../../contexts/AuthContext";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { unwrap } from "../../lib/api-envelope";
import { formatInstant, instantTenantDay, parseInstantDate, zonedParts, zonedWallClockToInstant } from "../../lib/calendar-date";
import { fmtCurrency, parseMoney } from "../../lib/display-labels";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { DhEmpty, DhPanel, DhSpec } from "../downhole/DownholeBits";
import { clearablePatch, formValue } from "../../components/rentals/rentalEdit";
import { FleetPickerNotice } from "../../components/rentals/FleetPickerNotice";
import { useBillHaulHourly } from "../../hooks/useBillHaulHourly";
import { isTrailerUnit } from "../../lib/hauling";
import type { RentalDispatch, RentalDispatchUpdateResponse } from "../../types/rental-dispatch";
import { NewInspectionDialog, type InspectionDispatchContext } from "../../components/rentals/NewInspectionDialog";

/**
 * One rental dispatch, /rentals/dispatches/:id (#815 PR F). Opened from the
 * dispatch board and from the order page's Dispatches list. The band carries
 * type, time, driver and asset count; the truck's label is long, so it sits in
 * the record.
 *
 * EDIT: PUT /surface-rentals/dispatches/:id. Absent keeps, a value sets, an
 * emptied field is sent as null and CLEARS (api#989); the API's
 * FIELD_NOT_CLEARABLE is shown as sent. Once a dispatch is completed
 * (delivered / returned) or cancelled only its NOTES may change: the form
 * offers notes alone, and a DISPATCH_LOCKED refusal is shown as sent.
 *
 * RE-PRICE (api#1014): a new truck, day or type re-prices the dispatch's
 * trucking load from the rate card. The response says so, and the page shows
 * it as a short note — never as an error, because the edit saved:
 *   rate_card_refusals  the kinds the card had no row for (left empty)
 *   load_rates_locked   "Load already signed; rates not changed"
 *   mob_fee_overlap     a mob / demob fee the rental invoice already bills
 */

/** RENTAL_ORDER_WRITE on the API: the same roles may change a dispatch here. */
const WRITE_ROLES = ["admin", "office_staff", "supervisor", "dispatcher"];
/** DISPATCH_LOCKED_STATUSES on the API: only notes change after these. */
const LOCKED = ["completed", "delivered", "returned", "cancelled"];
/** PATCH /dispatches/:id/haul-hours is RENTAL_BILLING_WRITE: admin and office staff. */
const HAUL_WRITE_ROLES = ["admin", "office_staff"];
/** Who may file a rental inspection (api#1068); a customer, portal or sales login is refused. */
const INSPECT_ROLES = ["admin", "office_staff", "supervisor", "dispatcher", "field_worker", "logistics"];
const TYPE: Record<string, string> = { delivery: "Delivery", pickup: "Pickup", transfer: "Transfer", swap: "Swap", relocation: "Relocation" };
const STATUS: Record<string, { label: string; fill: string }> = {
  pending: { label: "Pending", fill: "bg-fi-signal-amber" },
  scheduled: { label: "Scheduled", fill: "bg-fi-signal-blue" },
  in_transit: { label: "In transit", fill: "bg-fi-signal-blue" },
  completed: { label: "Completed", fill: "bg-fi-signal-green" },
  delivered: { label: "Delivered", fill: "bg-fi-signal-green" },
  returned: { label: "Returned", fill: "bg-fi-signal-green" },
  cancelled: { label: "Cancelled", fill: "bg-fi-signal-slate" },
};
const skinOf = (s: unknown) => (typeof s === "string" && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : { label: "—", fill: "bg-fi-signal-slate" });
const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));

const BAND_SECONDARY =
  "inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 bg-fi-brand px-4 text-sm font-bold transition hover:bg-white/10 disabled:opacity-50";
const SELECT = "h-9 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

interface FleetTruck { id: string; unit_number?: string; type?: string; year?: number | null; make?: string | null; model?: string | null }
interface FleetDriver { id: string; first_name?: string; last_name?: string }

/** The re-price outcome the last save reported, as a short note. */
export function repriceNote(res: RentalDispatchUpdateResponse | null | undefined): string[] {
  if (!res) return [];
  const lines: string[] = [];
  if (res.load_rates_locked) lines.push("Load already signed; rates not changed");
  if (Array.isArray(res.rate_card_refusals)) {
    lines.push(
      res.rate_card_refusals.length === 0
        ? "Load re-priced from the rate card"
        : `Load re-priced; not on the rate card: ${res.rate_card_refusals.map((r) => r.error).join(" ")}`,
    );
  }
  for (const o of res.mob_fee_overlap ?? []) {
    lines.push(`${o.asset_number} ${o.fee === "demob_fee" ? "demob" : "mob"} fee ${fmtCurrency(parseMoney(o.amount))} is already on the rental invoice`);
  }
  return lines;
}

export default function RentalDispatchDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(String((user as { role?: string } | null)?.role ?? ""));
  const [editing, setEditing] = useState(false);
  const [editingHaul, setEditingHaul] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const canInspect = INSPECT_ROLES.includes(String((user as { role?: string } | null)?.role ?? ""));
  const [note, setNote] = useState<string[]>([]);
  const { on: billHaul } = useBillHaulHourly();
  const canWriteHaul = HAUL_WRITE_ROLES.includes(String((user as { role?: string } | null)?.role ?? ""));

  const query = useQuery({
    queryKey: ["rental-dispatch", id],
    queryFn: async () => {
      const raw = unwrap<RentalDispatch>((await api.rentals.dispatch.get(id)).data);
      return raw && raw.id ? raw : null;
    },
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const d = failure ? null : query.data ?? null;
  const loading = query.isLoading && !failure;
  const skin = skinOf(d?.status);
  const locked = !!d && LOCKED.includes(String(d.status));
  const type = d ? TYPE[String(d.dispatch_type)] ?? "Dispatch" : "Dispatch";
  // Haul hours (api#1054): a trip with a truck, in a company that bills hauls by
  // the hour — or any recorded value, which is shown whatever the switch says.
  const haulApplies = !!d && billHaul && !!str(d.truck_id);
  const showHaul = haulApplies || str(d?.haul_hours) != null;
  // A New inspection started here is filed against this dispatch and its order (api#1068).
  const inspectFrom = useMemo<InspectionDispatchContext | undefined>(
    () =>
      d && str(d.order_id)
        ? {
            dispatch_id: String(d.id),
            dispatch_label: `${TYPE[String(d.dispatch_type)] ?? "Dispatch"} · ${str(d.order_number) ?? "—"}`,
            order_id: String(d.order_id),
            order_number: str(d.order_number) ?? "—",
            customer_name: str(d.customer_name) ?? "—",
            type: d.dispatch_type === "pickup" ? "post_return" : "pre_delivery",
          }
        : undefined,
    [d],
  );
  const assets = Array.isArray(d?.assets) ? (d!.assets as unknown[]).map((a) => (typeof a === "string" ? a : str((a as { asset_name?: string })?.asset_name) ?? "—")) : [];

  return (
    <div data-testid="rental-dispatch-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={d ? `Rental dispatch · ${skin.label}` : "Rental dispatch"}
        title={d ? `${type} · ${str(d.order_number) ?? "—"}` : loading ? "Loading…" : "Dispatch"}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          d && (canWrite || (haulApplies && canWriteHaul) || (canInspect && inspectFrom)) ? (
            <div className="flex flex-wrap items-center gap-2">
              {canInspect && inspectFrom && (
                <button type="button" className={BAND_SECONDARY} onClick={() => setInspecting(true)}>
                  <ClipboardCheck className="size-4" aria-hidden="true" />
                  New inspection
                </button>
              )}
              {haulApplies && canWriteHaul && (
                <button type="button" className={BAND_SECONDARY} onClick={() => setEditingHaul(true)}>
                  <Clock className="size-4" aria-hidden="true" />
                  Edit haul hours
                </button>
              )}
              {canWrite && (
                <button type="button" className={BAND_SECONDARY} onClick={() => setEditing(true)}>
                  <Pencil className="size-4" aria-hidden="true" />
                  {locked ? "Edit notes" : "Edit dispatch"}
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={
          !d
            ? undefined
            : [
                { icon: <Workflow className="size-4" aria-hidden="true" />, label: "Type", sub: skin.label, tone: "neutral", value: type },
                { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: "Scheduled", sub: "Tenant time", tone: "info", value: formatInstant(str(d.scheduled_date), timeZone) ?? "—" },
                { icon: <User className="size-4" aria-hidden="true" />, label: "Driver", sub: "Assigned", tone: "neutral", value: str(d.driver_name) ?? "—" },
                { icon: <Package className="size-4" aria-hidden="true" />, label: "Assets", sub: "On this dispatch", tone: "neutral", value: String(assets.length) },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading dispatch" />
            </div>
          ) : !d ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              {failure ? (
                <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this dispatch" />
              ) : (
                <DhEmpty>This dispatch does not exist, or it was deleted.</DhEmpty>
              )}
            </div>
          ) : (
            <>
              {note.length > 0 && (
                <section data-testid="reprice-note" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  {note.map((line) => <p key={line}>{line}</p>)}
                </section>
              )}
              <DhPanel eyebrow="Dispatch" title="Dispatch record" testId="rental-dispatch-record">
                <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-4 sm:grid-cols-2 md:grid-cols-4">
                  <DhSpec
                    label="Status"
                    value={<span className={`inline-block rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink ${skin.fill}`}>{skin.label}</span>}
                  />
                  <DhSpec
                    label="Order"
                    value={str(d.order_id) ? <Link className="underline underline-offset-2" to={`/rentals/orders/${encodeURIComponent(String(d.order_id))}`}>{str(d.order_number) ?? "—"}</Link> : "—"}
                  />
                  <DhSpec label="Customer" value={str(d.customer_name) ?? "—"} />
                  <DhSpec label="Completed" value={formatInstant(str(d.completed_date), timeZone) ?? "—"} />
                  <DhSpec label="Truck" value={str(d.truck_info) ?? "—"} />
                  {showHaul && <DhSpec label="Haul hours" value={str(d.haul_hours) != null ? `${str(d.haul_hours)} h` : "—"} />}
                  <DhSpec label="Pickup" value={str(d.pickup_address) ?? "—"} />
                  <DhSpec label="Deliver to" value={str(d.delivery_address) ?? "—"} />
                  <DhSpec label="Site contact" value={[str(d.contact_name), str(d.contact_phone)].filter(Boolean).join(" · ") || "—"} />
                  <DhSpec label="Assets" value={assets.length ? assets.join(", ") : "—"} />
                </dl>
                {str(d.special_instructions) && (
                  <p className="whitespace-pre-line border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                    <span className="block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Special instructions</span>
                    {str(d.special_instructions)}
                  </p>
                )}
                {str(d.notes) && (
                  <p data-testid="dispatch-notes" className="whitespace-pre-line border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                    <span className="block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Notes</span>
                    {str(d.notes)}
                  </p>
                )}
                {locked && <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">{skin.label}: only notes can change.</p>}
              </DhPanel>
            </>
          )}
        </div>
      </section>

      {inspectFrom && canInspect && <NewInspectionDialog open={inspecting} onOpenChange={setInspecting} fromDispatch={inspectFrom} />}
      {editingHaul && d && canWriteHaul && (
        <HaulHoursDialog dispatchId={String(d.id)} current={str(d.haul_hours)} onClose={() => setEditingHaul(false)} />
      )}
      {editing && d && canWrite && (
        <DispatchEditDialog dispatch={d} locked={locked} timeZone={timeZone} onClose={() => setEditing(false)} onSaved={setNote} />
      )}
    </div>
  );
}

/** The scheduled instant as the tenant's day and HH:MM, for the form. */
function scheduledParts(value: unknown, timeZone: string | null): { date: string; time: string } {
  const at = parseInstantDate(value);
  const day = instantTenantDay(value, timeZone);
  const p = at ? zonedParts(at, timeZone) : null;
  return { date: day ?? "", time: p ? `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}` : "" };
}

function DispatchEditDialog({
  dispatch,
  locked,
  timeZone,
  onClose,
  onSaved,
}: {
  dispatch: RentalDispatch;
  locked: boolean;
  timeZone: string | null;
  onClose: () => void;
  onSaved: (note: string[]) => void;
}) {
  const qc = useQueryClient();
  const fleetQuery = useQuery<{ trucks: FleetTruck[]; drivers: FleetDriver[] }>({
    queryKey: ["rentals-fleet"],
    queryFn: () =>
      api.rentals.fleet().then((r) => {
        const body = r.data;
        const payload = body && typeof body === "object" && "data" in body ? body.data : body;
        return { trucks: Array.isArray(payload?.trucks) ? payload.trucks : [], drivers: Array.isArray(payload?.drivers) ? payload.drivers : [] };
      }),
    enabled: !locked,
    staleTime: 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const fleetFailed = queryLoadFailure(fleetQuery) != null;
  // One list from the API; a trailer is pulled, not driven (api#1045).
  const units = fleetQuery.data?.trucks ?? [];
  const trucks = units.filter((x) => !isTrailerUnit(x.type));
  const trailers = units.filter((x) => isTrailerUnit(x.type));
  const drivers = useMemo(
    () => (fleetQuery.data?.drivers ?? []).map((x) => ({ id: x.id, name: `${x.first_name ?? ""} ${x.last_name ?? ""}`.trim() || "Driver" })),
    [fleetQuery.data],
  );
  const sched = scheduledParts(dispatch.scheduled_date, timeZone);
  const [before] = useState<Record<string, string>>(() => ({
    driver_id: formValue(dispatch.driver_id),
    truck_id: formValue(dispatch.truck_id),
    trailer_id: formValue(dispatch.trailer_id),
    pickup_address: formValue(dispatch.pickup_address),
    delivery_address: formValue(dispatch.delivery_address),
    contact_name: formValue(dispatch.contact_name),
    contact_phone: formValue(dispatch.contact_phone),
    special_instructions: formValue(dispatch.special_instructions),
    notes: formValue(dispatch.notes),
  }));
  const [form, setForm] = useState(before);
  const [date, setDate] = useState(sched.date);
  const [time, setTime] = useState(sched.time);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    setRefusal(null);
    // A done dispatch's form holds notes alone, so only notes can differ.
    const body: Record<string, unknown> = clearablePatch(before, form);
    if (date !== sched.date || time !== sched.time) {
      if (date === "") {
        body.scheduled_date = null;
      } else {
        // The tenant's wall clock (companies.time_zone), as the board's create does.
        const [y, m, dd] = date.split("-").map(Number);
        const [hh, mm] = (time || "00:00").split(":").map(Number);
        const at = zonedWallClockToInstant(y, m, dd, hh || 0, mm || 0, timeZone);
        if (!at) {
          setRefusal("Could not read the date and time in your company's time zone.");
          return;
        }
        body.scheduled_date = at.toISOString();
      }
    }
    // Only the ids go: PUT looks up driver_name / truck_info from them the
    // way POST does, and a cleared id clears its label (api#1065). The page
    // then re-reads the dispatch, so the label shown is the API's.
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const res = (await api.rentals.dispatch.update(String(dispatch.id), body)).data;
      toast.success("Dispatch saved");
      qc.invalidateQueries({ queryKey: ["rental-dispatch", String(dispatch.id)] });
      qc.invalidateQueries({ queryKey: ["rentals-order"] });
      qc.invalidateQueries({ queryKey: ["rentals-dispatches"] });
      onSaved(repriceNote(res));
      onClose();
    } catch (error) {
      // DISPATCH_LOCKED, FIELD_NOT_CLEARABLE, DISPATCH_CHANGED, a truck out of service: the API's words.
      setRefusal(getApiErrorMessage(error, "The dispatch was not saved."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{locked ? "Edit notes" : "Edit dispatch"}</DialogTitle>
        </DialogHeader>
        {locked && <p className="text-xs text-fieldiq-muted">This dispatch is done; only its notes can change.</p>}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="dispatch-edit-form">
          {!locked && (
            <>
              <div className="min-w-0">
                <Label htmlFor="dispatch-date">Scheduled day</Label>
                <Input id="dispatch-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="dispatch-time">Time</Label>
                <Input id="dispatch-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="dispatch-driver">Driver</Label>
                <select id="dispatch-driver" className={SELECT} value={form.driver_id} onChange={set("driver_id")}>
                  <option value="">None</option>
                  {form.driver_id && !drivers.some((x) => x.id === form.driver_id) && <option value={form.driver_id}>{str(dispatch.driver_name) ?? "Current driver"}</option>}
                  {drivers.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div className="min-w-0">
                <Label htmlFor="dispatch-truck">Truck</Label>
                <select id="dispatch-truck" className={SELECT} value={form.truck_id} onChange={set("truck_id")}>
                  <option value="">None</option>
                  {form.truck_id && !trucks.some((x) => x.id === form.truck_id) && <option value={form.truck_id}>{str(dispatch.truck_info) ?? "Current truck"}</option>}
                  {trucks.map((x) => <option key={x.id} value={x.id}>{x.unit_number || x.id} — {x.type || "Truck"}</option>)}
                </select>
              </div>
              <div className="min-w-0">
                <Label htmlFor="dispatch-trailer">Trailer</Label>
                <select id="dispatch-trailer" className={SELECT} value={form.trailer_id} onChange={set("trailer_id")}>
                  <option value="">None</option>
                  {form.trailer_id && !trailers.some((x) => x.id === form.trailer_id) && <option value={form.trailer_id}>Current trailer</option>}
                  {trailers.map((x) => <option key={x.id} value={x.id}>{x.unit_number || x.id}</option>)}
                </select>
              </div>
              {(fleetQuery.data || fleetFailed) && (
                <div className="sm:col-span-2">
                  <FleetPickerNotice failed={fleetFailed} trucks={trucks.length} drivers={drivers.length} />
                </div>
              )}
              {(
                [
                  ["pickup_address", "Pickup address"],
                  ["delivery_address", "Delivery address"],
                  ["contact_name", "Site contact"],
                  ["contact_phone", "Contact phone"],
                ] as const
              ).map(([key, label]) => (
                <div key={key} className="min-w-0">
                  <Label htmlFor={`dispatch-${key}`}>{label}</Label>
                  <Input id={`dispatch-${key}`} value={form[key]} onChange={set(key)} />
                </div>
              ))}
              <div className="min-w-0 sm:col-span-2">
                <Label htmlFor="dispatch-instructions">Special instructions</Label>
                <Textarea id="dispatch-instructions" value={form.special_instructions} onChange={set("special_instructions")} />
              </div>
            </>
          )}
          <div className="min-w-0 sm:col-span-2">
            <Label htmlFor="dispatch-notes">Notes</Label>
            <Textarea id="dispatch-notes" value={form.notes} onChange={set("notes")} />
          </div>
        </div>
        {!locked && <p className="text-xs text-fieldiq-muted">An emptied field is cleared.</p>}
        {refusal && (
          <p role="alert" data-testid="dispatch-edit-refusal" className="rounded-fi-control border border-fieldiq-border px-3 py-2 text-xs text-fieldiq-text">
            {refusal}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The office corrects a trip's haul hours (api#1054): PATCH
 * /dispatches/:id/haul-hours, admin and office staff. Blank clears (not
 * recorded); an explicit 0 is a real 0. Once the haul is on an invoice the API
 * refuses with HAUL_HOURS_INVOICED, shown here in its own words.
 */
function HaulHoursDialog({ dispatchId, current, onClose }: { dispatchId: string; current: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(current ?? "");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setRefusal(null);
    setBusy(true);
    try {
      await api.rentals.dispatch.haulHours(dispatchId, value.trim() === "" ? null : Number(value));
      toast.success("Haul hours saved");
      qc.invalidateQueries({ queryKey: ["rental-dispatch", dispatchId] });
      qc.invalidateQueries({ queryKey: ["rentals-billing"] });
      onClose();
    } catch (error) {
      setRefusal(getApiErrorMessage(error, "The haul hours were not saved."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <div role="dialog" aria-modal="true" aria-label="Edit haul hours">
          <DialogHeader>
            <DialogTitle>Edit haul hours</DialogTitle>
          </DialogHeader>
          <Label htmlFor="haul-hours-edit">Haul hours</Label>
          <Input id="haul-hours-edit" type="number" inputMode="decimal" min="0" max="999.99" step="0.25" value={value} onChange={(e) => setValue(e.target.value)} />
          <p className="mt-1 text-xs text-fieldiq-muted">Blank means not recorded; the haul can't be invoiced until it has hours.</p>
          {refusal && (
            <p role="alert" data-testid="haul-hours-refusal" className="mt-2 rounded-fi-control border border-fieldiq-border px-3 py-2 text-xs text-fieldiq-text">{refusal}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy || value === (current ?? "")}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
